import { Point } from "@/lib/math/models/point"
import Victor from 'victor';

const LINE_SEGMENT_THRESHOLD = 1e-6;

export const mathUtils = {

    /**
     * 
     * @param {[Point, Point]} line 
     * @param {number} distance
     * @param {'left' | 'right'} side 
     * @returns 
     */
    getParallelLineSegment(line, distance, side) {
        const startPoint = Victor.fromObject(line[0]);
        const endPoint = Victor.fromObject(line[1]);
        const vLine = endPoint.clone().subtract(startPoint);
        const vPerpendicular = side == 'right' ? vLine.clone().rotate(-Math.PI / 2).normalize() : vLine.clone().rotate(Math.PI / 2).normalize();
        const newStartPoint = side == 'right' ? startPoint.clone().subtract(vPerpendicular.clone().multiplyScalar(distance)) : startPoint.clone().subtract(vPerpendicular.clone().multiplyScalar(distance));
        const newEndPoint = side == 'right' ? endPoint.clone().subtract(vPerpendicular.clone().multiplyScalar(distance)) : endPoint.clone().subtract(vPerpendicular.clone().multiplyScalar(distance));
        return [newStartPoint.toObject(), newEndPoint.toObject()];
    },

    /**
     * 
     * @param {*} line 
     * @param {'left' | 'right' | 'both' | undefined} direction 
     * @returns 
     */
    getPerpendicularLineSegment(line, direction = undefined) {
        var startPoint = Victor.fromObject(line[0]);
        // It will be based on the end point.
        var endPoint = Victor.fromObject(line[1]);
        var distance = endPoint.distance(startPoint);
        // Uso -PI para garantir que é a minha esquerda (vista na tela).
        var vPerpendicular = endPoint.clone().subtract(startPoint).rotate(-Math.PI / 2).normalize();
        switch (direction) {
            case 'left':
                return [
                    endPoint.toObject(),
                    endPoint.clone().add(vPerpendicular.multiplyScalar(distance)).toObject()
                ];
            case 'right':
                return [
                    endPoint.toObject(),
                    endPoint.clone().add(vPerpendicular.invert().multiplyScalar(distance)).toObject()
                ];
            case 'both':
            default:
                // Perpendicular centralizada
                return [
                    endPoint.clone().add(vPerpendicular.multiplyScalar(distance)).toObject(),
                    endPoint.clone().add(vPerpendicular.invert().multiplyScalar(distance)).toObject()
                ]
        }

        // var perpendicularVector = endPoint.clone().subtract(startPoint).rotate(Math.PI / 2).normalize();
        // var newStartPoint, newEndPoint;
        // if (direction == 'start') {
        //     newStartPoint = endPoint.clone().add(perpendicularVector.clone().multiplyScalar(distance * multiplierFactor));
        //     newEndPoint = endPoint.clone();
        // } else if (direction == 'end') {
        //     newStartPoint = endPoint.clone();
        //     newEndPoint = endPoint.clone().add(perpendicularVector.clone().multiplyScalar(distance * multiplierFactor));
        // } else {
        //     newStartPoint = endPoint.clone().add(perpendicularVector.clone().multiplyScalar(distance * multiplierFactor));
        //     newEndPoint = endPoint.clone().add(perpendicularVector.clone().invert().multiplyScalar(distance * multiplierFactor));
        // }
        // return [newStartPoint.toObject(), newEndPoint.toObject()];
    },

    /**
     * Extende uma segmentacao pelo endpoint usando um numero multiplicador.
     * @param {[Point, Point]} segment 
     * @param {number} distanceMultiplier 
     * @param {'start' | 'end' | 'both'} direction
     * @returns {[Point, Point]}
     */
    extendLineSegment(segment, distanceMultiplier, direction = 'end') {
        var startPoint = Victor.fromObject(segment[0]);
        var endPoint = Victor.fromObject(segment[1]);
        var desiredDistance = (endPoint.distance(startPoint) * distanceMultiplier);
        var newStartPoint, newEndPoint;
        switch (direction) {
            case 'start':
                newStartPoint = endPoint.clone().add(startPoint.clone().subtract(endPoint).normalize().multiplyScalar(desiredDistance));
                return [
                    newStartPoint.toObject(),
                    endPoint.toObject()
                ];
            case 'end':
                newEndPoint = startPoint.clone().add(endPoint.clone().subtract(startPoint).normalize().multiplyScalar(desiredDistance));
                return [
                    startPoint.toObject(),
                    newEndPoint.toObject()
                ];
            case 'both':
                newStartPoint = endPoint.clone().add(startPoint.clone().subtract(endPoint).normalize().multiplyScalar(desiredDistance));
                newEndPoint = startPoint.clone().add(endPoint.clone().subtract(startPoint).normalize().multiplyScalar(desiredDistance));
                return [
                    newStartPoint.toObject(),
                    newEndPoint.toObject()
                ]
        }
        return segment;
    },

    /**
     * 
     * @param {Array<{x: number, y: number}>} lineA 
     * @param {Array<{x: number, y: number}>} lineB 
     * @returns {{x: number, y: number}}
     */
    calculateIntersectionPointBetweenLines(lineA, lineB) {
        // Line = [startPoint, endPoint]
        const pLineA = Victor.fromObject(lineA[0]);
        const vLineA = Victor.fromObject(lineA[1]).subtract(pLineA);
        const pLineB = Victor.fromObject(lineB[0]);
        const vLineB = Victor.fromObject(lineB[1]).subtract(pLineB);

        const denom = vLineA.cross(vLineB);
        if (Math.abs(denom) < 1e-10)
            return null;

        const t = pLineB.clone().subtract(pLineA).cross(vLineB) / denom;
        return pLineA.clone().add(vLineA.clone().multiplyScalar(t)).toObject();
    },

    /**
     * Encontra o ponto mais próximo de um segmento de reta
     * @description Retorna o ponto mais próximo do segmento de reta
     * @param {Array<{x: number, y: number}>} points 
     * @param {Array<{x: number, y: number}>} lineSegment 
     * @returns {{point: {x: number, y: number} | null, index: number, distance: number | undefined}}
     */
    findClosestPointFromLineSegment(points, lineSegment) {
        const MAX_DISTANCE = 10;
        let candidatePoints = [];
        // console.log(' => lineSegment: ', JSON.stringify(lineSegment));
        for (let i = 0; i < points.length; i++) {
            let distance = this.getPointDistanceFromLineSegment(points[i], lineSegment);
            // console.log(JSON.stringify(points[i]), ' => distance: ', distance);
            if (distance < MAX_DISTANCE) {
                candidatePoints.push({
                    point: points[i],
                    index: i,
                    distance: distance
                })
            }
        }

        if (candidatePoints.length == 0) {
            // console.log(' => candidatePoints: none')
            return {
                point: null,
                index: -1,
                distance: undefined
            }
        }
        candidatePoints = candidatePoints.sort((a, b) => a.distance - b.distance)
        // console.log('findClosestPointsFromLineSegment candidates: ', JSON.stringify(candidatePoints));
        let closestPoint = candidatePoints[0]
        let filteredCandidates = candidatePoints.filter(candidate => Math.floor(candidate.distance * 10) === Math.floor(closestPoint.distance * 10))
        // If exists more than one really close candidates
        if (filteredCandidates.length > 1) {
            // Check which distance is greater comparing to the line
            const comparingPoint = Victor.fromObject(lineSegment[0])
            filteredCandidates = filteredCandidates.sort((a, b) => {
                return Victor.fromObject(b.point).subtract(comparingPoint).lengthSq() - Victor.fromObject(a.point).subtract(comparingPoint).lengthSq()
            })
        }
        // console.log(' => filteredCandidates: ', JSON.stringify(filteredCandidates));

        return filteredCandidates.length == 0 ? closestPoint : filteredCandidates[0]
    },

    /**
     * Calcula a distância de um ponto a um segmento de reta
     * @description Retorna a distância do ponto ao segmento de reta
     * @param {{x: number, y: number}} point 
     * @param {Array<{x: number, y: number}>} lineSegment 
     * @returns {number}
     */
    getPointDistanceFromLineSegment(point, lineSegment) {
        var startPoint = Victor.fromObject(lineSegment[0]);
        var endPoint = Victor.fromObject(lineSegment[1]);
        var targetPoint = Victor.fromObject(point);
        // Direction vector of the line segment.
        var vLine = endPoint.clone().subtract(startPoint);
        var vTarget = targetPoint.clone().subtract(startPoint);

        var vLineSqMag = vLine.dot(vLine);
        // If vLine is a single point.
        if (vLineSqMag === 0) {
            return targetPoint.distance(startPoint);
        }
        var t = vTarget.dot(vLine) / vLineSqMag;
        var iLine = startPoint.clone().add(vLine.clone().multiplyScalar(t));
        return targetPoint.distance(iLine);
    },

    pointIntersectsLineSegment(point, lineSegment) {
        return this.getPointDistanceFromLineSegment(point, lineSegment) < LINE_SEGMENT_THRESHOLD;
    },

    /**
     * 
     * @param {Point} ptA 
     * @param {Point} ptB 
     * @returns {Point}
     */
    getMidPoint(ptA, ptB) {
        return Victor.fromObject(ptA)
            .add(Victor.fromObject(ptB))
            .divideScalar(2)
            .toObject();
    },

    /**
     * @param {Point} point 
     * @param {Point} segmentPointA 
     * @param {Point} segmentPointB 
     * @returns {'left' | 'right' | 'center'}
     */
    pointSideRelatedToLineSegment(point, segmentPointA, segmentPointB) {
        const a = segmentPointA;
        const b = segmentPointB;
        const determinant = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x)
        if (determinant == 0)
            return 'center';
        return determinant > 0 ? 'left' : 'right';
    },

    /** 
     * Acha a projecao de P no segmento/vetor vAB:
        Factor < 0 => projP_vAB estah antes do segmento AB (mais prox de pA)
        Factor > 1 => projP_vAB estah depois do segmento AB (mais prox de pB) 
        0 <= Factor <= 1 => projP_vAB estah dentro do segmento AB 

        * @param {Victor} pP
        * @param {Victor} pA
        * @param {Victor} vAB
        * @returns {{proj: Victor, factor: number}}
    */
    projP_vAB(pP, pA, vAB) {
        var factor,
            vAP, proj_vAP_vAB, projP_vAB;

        /* 
            pP - ponto qq
            pA - 1o ponto do segmento AB
            vAB - segmento AB
            vAP - segmento AP
            proj_vAP_vAB - projecão de vAP em vAB 
                => vAB . vAP / vAB . vAB * vAB
            ref: https://en.wikipedia.org/wiki/Vector_projection
                    https://www.youtube.com/watch?v=GnvYEbaSBoY 
        */
        vAP = pP.clone().subtract(pA);
        factor = vAB.clone().dot(vAP) / vAB.clone().dot(vAB);
        proj_vAP_vAB = vAB.clone().multiply(Victor(factor, factor));
        projP_vAB = pA.clone().add(proj_vAP_vAB);

        return { proj: projP_vAB, factor: factor }
    },

    /**
     * 
     * @param {Point} point 
     * @param {[Point, Point]} segment 
     */
    getPointProjectionOnSegment(point, segment) {
        const vLineSegment = Victor.fromObject(segment[1]).subtract(Victor.fromObject(segment[0]))
        return mathUtils.projP_vAB(Victor.fromObject(point), Victor.fromObject(segment[0]), vLineSegment);
    },

    /**
     * 
     * @param {Array<Point>} pts 
     * @returns 
     */
    getBoundingRect(pts) {
        if (!pts || pts.length === 0) {
            return { x: 0, y: 0, width: 0, height: 0 };
        }
      
        let minX = pts[0].x;
        let maxX = pts[0].x;
        let minY = pts[0].y;
        let maxY = pts[0].y;
      
        pts.forEach(pt => {
            if (pt.x < minX) minX = pt.x;
            if (pt.x > maxX) maxX = pt.x;
            if (pt.y < minY) minY = pt.y;
            if (pt.y > maxY) maxY = pt.y;
        });
      
        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
        };
    }, 
}